import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DataGridComponent } from '@app/shared/custom-component/data-grid/data-grid.component';
import { TabsComponent } from '@app/shared/custom-component/tabs/tabs.component';
import { MaterialUIModule } from '@app/shared/modules/material-ui/material-ui.module';
import { HttpService } from '@core/services/http.service';
import { CommonService } from '@app/shared/services/common.service';
import { AlertService } from '@core/services/alert.service';
import { take, takeUntil } from 'rxjs';
import { Subject } from 'rxjs';
import { DataGridVO } from '@app/shared/custom-component/data-grid/data-grid.model';
import * as ExcelJS from 'exceljs';
import * as FileSaver from 'file-saver';
import { LoaderService } from '@core/services/loader.service';
import { API_ENDPOINTS } from '@app/shared/constants/apiconstants';
import { EmailLogsApiResponse, EmailLog } from './message-center.interface';
import { ModalComponent } from '@app/shared/custom-component/modal/modal.component';
import { modalButtonTitles, ModalConfig, modalHeading } from '@app/shared/constants/modal.model';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { modalContent } from '@app/shared/constants/modal.model';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { InAppMessageModule } from '@app/pages/in-app-message/in-app-message.module';
const MESSAGE_CENTER_CONSTANTS = {
  METADATA_TYPE: 'message-center',
  DEFAULT_ACTIVE_TAB: 'message'
};

@Component({
  selector: 'app-message-center',
  templateUrl: './message-center.component.html',
  styleUrls: ['./message-center.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    DataGridComponent,
    TabsComponent,
    MaterialUIModule,
    InAppMessageModule
  ]
})
export class MessageCenterComponent implements OnInit, OnDestroy {
  tabs: string[] = [];
  activeTab: any = MESSAGE_CENTER_CONSTANTS.DEFAULT_ACTIVE_TAB;
  showColumnChooser: boolean = false;
  allColumns: { key: string; label: string; disabled?: boolean }[] = [];
  visibleColumns: { [key: string]: boolean } = {};
  messageData: any[] = [];
  emailLogs: EmailLog[] = [];
  headers: any[] = [];
  hoverTooltipId: string | null = null;
  isLoading: boolean = false;
  private destroy$ = new Subject<void>();
  mandatoryColumns: Set<string> = new Set();
  sanitizedEmailHtml: SafeHtml | null = null;

  @ViewChild('actionsTemplate') actionsTemplate?: TemplateRef<any>;
  @ViewChild(DataGridComponent) dataGridComponent?: DataGridComponent;
  customModal?: NgbModalRef;

  dataGridConfig: DataGridVO = DataGridVO.create({
    role: this.activeTab,
    activeTab: this.activeTab,
    data: this.messageData,
    visibleColumns: this.visibleColumns,
    showActionsColumn: true,
    showQueueActions: true,
    disableApplicationClick: false,
    showPaging: false,
    hidePager: true
  });

  constructor(
    private commonService: CommonService,
    private httpService: HttpService,
    private loaderService: LoaderService,
    private alertService: AlertService,
    private modalService: NgbModal,
    private sanitizer: DomSanitizer
  ) {
    this.updateDataGridConfig();
  }
  ngOnInit(): void {
    this.fetchMessageCenterTabs();
    this.loadColumnMetadata(this.activeTab);
  }

  private fetchMessageCenterTabs(): void {
    this.commonService
      .getMetaData(MESSAGE_CENTER_CONSTANTS.METADATA_TYPE)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.tabs = res.tabs || [];
        },
        error: (_err: any) => {
          this.alertService.error('Failed to load tabs');
        }
      });
  }

  private updateDataGridConfig(): void {
    this.dataGridConfig = DataGridVO.create({
      role: this.activeTab,
      activeTab: this.activeTab,
      data: this.messageData,
      visibleColumns: this.visibleColumns,
      showActionsColumn: true,
      showQueueActions: true,
      disableApplicationClick: this.showColumnChooser,
      customHeaders: this.headers
    });
  }

  private initVisibleColumns(): void {
    if (!this.visibleColumns) {
      this.visibleColumns = {};
    }
    this.mandatoryColumns.clear();
    this.mandatoryColumns.add('subject');
    this.allColumns.forEach(c => {
      if (typeof this.visibleColumns[c.key] === 'undefined') {
        this.visibleColumns[c.key] = true;
      }
    });
    this.updateDataGridConfig();
  }

  selectTab(tabKey: string): void {
    this.activeTab = tabKey;
    this.loadColumnMetadata(tabKey);
    this.updateDataGridConfig();
  }

  onTabSelected(tabKey: string): void {
    this.selectTab(tabKey);
  }

  loadColumnMetadata(tabKey: string): void {
    if (!tabKey) return;

    // Keep secure messaging (message) tab blank
    if (tabKey === 'message') {
      this.allColumns = [];
      this.headers = [];
      this.messageData = [];
      this.visibleColumns = {};
      this.mandatoryColumns.clear();
      this.updateDataGridConfig();
      return;
    }

    // Map tab key to correct header key
    const headerKey = tabKey === 'message' ? 'secure_messaging' : tabKey;
    this.commonService
      .getDataMessage(headerKey)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (headers: any) => {
          if (Array.isArray(headers)) {
            this.mandatoryColumns.clear();
            if (tabKey === 'email') {
              this.mandatoryColumns.add('subject');
            }
            this.allColumns = headers.map((h: any) => ({
              key: h.dataField,
              label: h.caption || h.dataField,
              disabled: tabKey === 'email' && this.mandatoryColumns.has(h.dataField)
            }));
            this.headers = headers;
          }
          this.initVisibleColumns();
          this.updateDataGridConfig();
          this.fetchMessageData();
        },
        error: (_err: any) => {
          this.alertService.error('Failed to load column metadata');
        }
      });
  }

  // Utility method to retrieve user ID -

  private getUserId(): string | null {
    let userObj = this.commonService.getCurrentUserData();

    if (!userObj) {
      const userFromSession = sessionStorage.getItem('user');
      if (userFromSession) {
        userObj = JSON.parse(userFromSession);
      }
    }

    if (!userObj) {
      return null;
    }

    return userObj?.id || userObj?.user_id || userObj?.userId || null;
  }

  fetchMessageData(): void {
    const userId = this.getUserId();

    if (!userId) {
      this.alertService.error('User information not found. Please login again.');
      return;
    }
    this.isLoading = true;
    this.loaderService.show();
    const payload = { userId };
    //const payload = { userId: '232' };
    this.httpService
      .post(API_ENDPOINTS.EMAIL_LOGS, payload)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: EmailLogsApiResponse) => {
          if (response?.status === 'success' && response?.data?.data?.p_email_logs) {
            const data = response.data?.data?.p_email_logs;
            this.mapEmailLogsData(data);
          } else {
            console.error('Invalid response structure:', response);
          }
        },
        error: error => {
          console.error('Failed to load email logs:', error);
          this.alertService.error('Failed to load email logs. Please try again.');
          this.isLoading = false;
          this.loaderService.hide();
        },
        complete: () => {
          this.isLoading = false;
          this.loaderService.hide();
        }
      });
  }

  private mapEmailLogsData(data: any): void {
    // Map Email Logs Data
    this.emailLogs = data.map((log: any) => ({
      id: log.strEmailLogId,
      personId: log.strPersonId,
      applicationId: log.strApplicationId,
      dateSent: new Date(log.dtDateEmailSent),
      recipientName: log.strRecipientName,
      subject: log.strSubject,
      constent: log.email_content,
      status: log.strStatus,
      userId: log.strUserId,
      createdBy: log.strCreatedBy,
      createdDate: new Date(log.dtCreatedDate),
      updatedBy: log.strUpdatedBy,
      updatedDate: new Date(log.dtupdatedDate),
      isActive: log.boolIsActive,
      sender: log.strSender,
      templateUsed: log.template_used,
      templateData: log.template_data,
      emailBody: log.email_body,
    }));

    // Load the email logs data into the grid after mapping
    this.loadEmailLogsData();
  }

  loadEmailLogsData(): void {
    // Load Email Logs data from API response
    if (Array.isArray(this.emailLogs) && this.emailLogs.length > 0) {
      this.messageData = this.emailLogs.map(log => ({
        id: log.id,
        date_sent: log.dateSent, 
        recipient: log.recipientName, 
         subject: this.truncateToWords(log.subject, 10),
        status: log.status === 'Sent' || log.status === 'sent' ? 'Delivered' : log.status,
        sender: log.sender
      }));
    } else {
      this.messageData = [];
    }
    this.updateDataGridConfig();
  }
  private truncateToWords(text: string, wordCount: number): string {
    if (!text) return '';
    const words = text.split(' ');
    if (words.length > wordCount) {
      return words.slice(0, wordCount).join(' ') + '...';
    }
    return text;
  }

  closeColumnChooser(): void {
    this.showColumnChooser = false;
    this.updateDataGridConfig();
  }

  onColumnClick(column: any): void {
    if (column.disabled || (this.mandatoryColumns.has(column.key) && this.visibleColumns[column.key])) {
      return;
    }
    this.visibleColumns[column.key] = !this.visibleColumns[column.key];
    this.updateDataGridConfig();
  }

  onColumnKey(event: any, column: any): void {
    if (event.key === 'Enter') {
      this.onColumnClick(column);
    }
  }

  downloadExcel(): void {
    // Get filtered data from the grid
    const filteredData = this.dataGridComponent?.getFilteredDataFromGrid() || this.messageData;

    if (filteredData.length === 0) {
      this.alertService.warn('No data available to export');
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(this.activeTab);

    // Add headers
    const headers = this.allColumns.filter(col => this.visibleColumns[col.key] !== false).map(col => col.label);

    worksheet.addRow(headers);

    // Add data rows (using filtered data)
    filteredData.forEach(item => {
      const row = this.allColumns.filter(col => this.visibleColumns[col.key] !== false).map(col => item[col.key] || '');
      worksheet.addRow(row);
    });

    // Style header row
    worksheet.getRow(1).font = { bold: true };
    worksheet.getRow(1).fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFD3D3D3' }
    };

    // Adjust column widths
    worksheet.columns.forEach(column => {
      column.width = 20;
    });

    workbook.xlsx.writeBuffer().then(buffer => {
      const fileName = `message-center-${this.activeTab.replace(/\s+/g, '_')}.xlsx`;
      FileSaver.saveAs(new Blob([buffer]), fileName);
    });
  }

  // ==================== EMAIL VIEW MODAL HANDLERS ====================
  viewMessage(messageData: any): void {
    const emailLogId = messageData.id;
    const userId = this.getUserId();

    if (!emailLogId || !userId) {
      this.alertService.error('Email information is missing.');
      return;
    }
     const emailLog = this.emailLogs.find(log => log.id === emailLogId);
    
    // Detect n8n email: template_used is null, empty string, or string "null"
    const templateUsed = emailLog?.templateUsed;
    const templateUsedStr = String(templateUsed || '').trim().toLowerCase();
    const isN8nEmail = !templateUsed || templateUsedStr === '' || templateUsedStr === 'null';

    if (isN8nEmail) {
      // Handle n8n emails (use email_body directly)
      if (!emailLog) {
        this.alertService.error('Email information not found.');
        return;
      }

      let emailBody = String(emailLog.emailBody ?? '-');
      
      // Remove leading '=' characters
      emailBody = emailBody.replace(/^=+/, '').trim();
      
      // Apply template data substitution if available
      if (emailLog.templateData) {
        emailBody = this.applyTemplateDataToBody(emailBody, emailLog.templateData);
      }
      
      // Enrich styles for n8n email
      emailBody = this.enrichEmailBodyStyles(emailBody, true);
      
      this.showEmailModal(
        messageData.subject,
        emailLog.recipientName,
        emailBody,
        emailLog.dateSent,
        emailLog.subject,
        true
      );
    } else {

    this.loaderService.show();

    // Call the render endpoint to get the email HTML
    this.httpService
    .get(`${API_ENDPOINTS.EMAIL_LOGS}/${emailLogId}/render?userId=${userId}`)
    .pipe(take(1), takeUntil(this.destroy$))
    .subscribe({
      next: (response: any) => {
        this.loaderService.hide();
        // Access nested data structure correctly
        if (response?.data?.data?.html) {
          const emailLog = this.emailLogs.find(log => log.id === emailLogId);
          const fullSubject = emailLog?.subject || messageData.subject;
          this.showEmailModal(
            messageData.subject,
            response.data.data.recipientName,
            response.data.data.html,
            response.data.data.dateSent,
            fullSubject
          );
        } else {
          this.alertService.error('Failed to load email content.');
          console.error('Invalid response structure:', response);
        }
      },
      error: (error: any) => {
        this.loaderService.hide();
        console.error('Error fetching email template:', error);
        this.alertService.error('Failed to load email content. Please try again.');
      }
    });
  }
  }

  private showEmailModal(
    subject: string,
    recipientName: string,
    htmlContent: string,
    dateSent: string | Date,
    fullSubject: string,
    isN8nEmail: boolean = false
  ): void {
    const options: NgbModalOptions = {
      backdrop: 'static',
      centered: true,
      size: 'lg'
    };

    const formattedDate = new Date(dateSent).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });

    let processedHtmlContent = isN8nEmail ? htmlContent : this.handleCidImages(htmlContent);
    this.sanitizedEmailHtml = this.sanitizer.bypassSecurityTrustHtml(processedHtmlContent);

    this.customModal = this.modalService.open(ModalComponent, options);
    const modalConfig: ModalConfig = {
      heading: modalHeading.viewMessageCenter,
      buttons: [
        { text: modalButtonTitles.close, code: 'N', btnOutline: true }
      ],
      modalContentArray: [
         { content: `<strong>Subject:</strong> <span title="${fullSubject || subject}">${subject}</span>` },
        // { content: `<strong>Recipient:</strong> ${recipientName}`, contentClass: 'info-box' },
        { content: `<strong>Date Sent:</strong> ${formattedDate}`, contentClass: 'info-box' },
        { content: this.sanitizedEmailHtml, contentClass: 'email-content-box', isSafeHtml: true }
      ],
      hasTemplate: true,
      scrollable: true
    };
    this.customModal.componentInstance.modalConfig = modalConfig;
  }

   private handleCidImages(htmlContent: string): string {
    if (!htmlContent) return htmlContent;

    // Remove leading/trailing whitespace from HTML content
    let processed = htmlContent.trim();

    // Add CSS to remove left padding/margin from email body elements
    const cleanupStyles = `
      <style>
        body, p, div, span, table { margin-left: 0 !important; padding-left: 0 !important; }
        .email-content-box { padding-left: 0 !important; margin-left: 0 !important; }
      </style>
    `;

    // Replace CID image references with Nevada education logo URL and add sizing
    const eduLogoUrl = 'https://nevadadocs.blob.core.windows.net/customuserflow/edu-logo.svg';
    processed = processed.replace(
      /src="cid:[^"]*"/gi,
      `src="${eduLogoUrl}" width="539.59" height="90.59" style="width: 539.59px; height: 90.59px; max-width: 100%; height: auto;"`
    );

    // Also handle class-based buttons with inline styling
   processed = processed.replace(
      /(<td|<th)([^>]*class="[^"]*custom-table2[^"]*"[^>]*)>/gi,
      '$1$2 style="padding-left: 0 !important; margin-left: 0 !important;">'
    );

    // Prepend cleanup styles if not already present
    if (!processed.includes('<style')) {
      processed = cleanupStyles + processed;
    }

    return processed;
  }
 private applyTemplateDataToBody(html: string, templateData: any): string {
    if (!html) return html;

    // Parse templateData if it's a JSON string
    let data = templateData;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (e) {
        console.error('Failed to parse templateData JSON:', e);
        return html;
      }
    }

    if (!data || typeof data !== 'object') return html;

    let result = html;
    
    // Helper to escape regex special characters
    const escapeRegex = (str: string): string => {
      return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };
    
    // Helper to convert camelCase to snake_case
    const camelToSnake = (str: string): string => {
      return str.replace(/([A-Z])/g, '_$1').toLowerCase();
    };
    
    // Helper to convert snake_case to camelCase
    const snakeToCamel = (str: string): string => {
      return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    };
    
    // Helper to convert to lowercase
    const toLower = (str: string): string => str.toLowerCase();
    
    // Build normalized key lookup map
    // Map: normalized_key → original_value
    const keyMap = new Map<string, string>();
    for (const [key, value] of Object.entries(data)) {
      if (value !== null && value !== undefined && typeof value !== 'object') {
        const stringValue = String(value ?? '').trim();
        const normalizedKey = toLower(key); // Store using lowercase as key
        keyMap.set(normalizedKey, stringValue);
      }
    }
    
    console.log('🔑 Template Data Received:', data);
    console.log('🗺️ Key Map Built:', Array.from(keyMap.entries()));
    
    // Find all placeholder patterns in HTML and replace them
    // Pattern: {{$json["key"]}} or {{$json['key']}} or {{key}}
    const placeholderRegex = /{{\s*(?:\$json\[\s*["'])?([^"'\]{}]+)(?:["']\s*\])?\s*}}/gi;
    
    let matchCount = 0;
    result = result.replace(placeholderRegex, (match, fieldName) => {
      matchCount++;
      const fieldNameTrimmed = fieldName.trim();
      console.log(`\n📍 Match ${matchCount}: "${match}"`);
      console.log(`   Extracted fieldName: "${fieldNameTrimmed}"`);
      
      // Try exact match (case-insensitive)
      const lowerFieldName = toLower(fieldNameTrimmed);
      console.log(`   1️⃣ Exact lowercase match: "${lowerFieldName}"`);
      if (keyMap.has(lowerFieldName)) {
        const value = keyMap.get(lowerFieldName);
        console.log(`   ✅ FOUND: "${value}"`);
        return value || match;
      }
      
      // Try converting snake_case to camelCase
      const camelFromSnake = snakeToCamel(fieldNameTrimmed);
      const camelLower = toLower(camelFromSnake);
      console.log(`   2️⃣ Snake→Camel: "${fieldNameTrimmed}" → "${camelFromSnake}" → "${camelLower}"`);
      if (keyMap.has(camelLower)) {
        const value = keyMap.get(camelLower);
        console.log(`   ✅ FOUND: "${value}"`);
        return value || match;
      }
      
      // Try converting camelCase to snake_case
      const snakeFromCamel = camelToSnake(fieldNameTrimmed);
      const snakeLower = toLower(snakeFromCamel);
      console.log(`   3️⃣ Camel→Snake: "${fieldNameTrimmed}" → "${snakeFromCamel}" → "${snakeLower}"`);
      if (keyMap.has(snakeLower)) {
        const value = keyMap.get(snakeLower);
        console.log(`   ✅ FOUND: "${value}"`);
        return value || match;
      }
      
      // No match found, return original placeholder
      console.log(`   ❌ NO MATCH - Returning original: "${match}"`);
      return match;
    });
    
    console.log(`\n✨ Template substitution complete. Total matches: ${matchCount}`);
    
    return result;
  }

  private enrichEmailBodyStyles(html: string, isN8nEmail: boolean = false): string {
    if (!html) return html;

    // Replace class-based buttons with inline-styled buttons
    html = html.replace(
      /<a\s+([^>]*?)class="btn"([^>]*)>/gi,
      '<a $1 style="display: inline-block; padding: 10px 20px; background: #005A9C; color: white !important; text-decoration: none; border-radius: 4px; font-weight: 500; font-family: sans-serif; font-size: 14px;" $2>'
    );

    // Replace cid: image sources with Nevada education logo URL
    const eduLogoUrl = 'https://nevadadocs.blob.core.windows.net/customuserflow/edu-logo.svg';
    html = html.replace(/src="cid:[^"]*"/gi, `src="${eduLogoUrl}"`);

    // Enhance logo image sizing
    html = html.replace(
      /(<img\s+[^>]*?class="logo"[^>]*?)\s*\/?>/gi,
      '$1 style="max-width: 250px; width: 100%; height: auto; display: block; margin-bottom:10px;" />'
    );

    // Add sizing to all images
    html = html.replace(
      /(<img\s+(?!.*style)[^>]*?)\s*\/?>/gi,
      '$1 style="max-width: 100%; height: auto;" />'
    );

    return html;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}